import { Logger } from '@nextgen-web-framework/core';
import { handleRequest } from '../../transformers';
import { type CustomRequestOptions, ProjectionType } from '../../types';
import { logObject } from '../../utils';
import { EN_US } from '../config/constants';
import {
  categoryOverview,
  duplicateChannel,
  getCategoryList,
  getChannelList,
  getInventoryList,
  getPriceList,
  getProductByProductIdOrItemCode,
  getProductSearchReqObj,
  getRedirectionLinkForContentStack,
  publishChannel,
  subCategory,
} from '../config/productBackOfficeEndPoints';
import type {
  Category,
  PcmMetadataResponse,
  ProductData,
  ProductPublishRequest,
  Products,
  ResponseCategories,
  ResponseInventories,
} from '../models';
import { ItemResponse } from '../models/pdp/models/ItemResponse';

const API_VERSION_V1 = 'v1';
const API_VERSION_V2 = 'v2';

type IProductAPI = {
  productId?: string;
  projections?: string;
  itemCode?: string;
  language?: string;
  isAlias?: boolean;
  channel?: string;
};

type IDuplicate = {
  vertical?: string;
  channel?: string;
};

type IProductPriceAPI = IDuplicate & {
  page?: string;
  limit?: string;
  productId?: string;
  itemNumber?: string;
  userSegment?: string;
  countryCode?: string;
  projections?: string;
  currency?: string;
  language?: string;
  channel?: string;
};

type IProductPaginationAPI = {
  page?: string;
  limit?: string;
  projections?: string;
  type?: string;
};

type IProductInventoryAPI = IDuplicate & {
  itemNumber?: string;
  locationId?: string;
  lastEvaluatedId?: string;
  projection?: string;
  country?: string;
  limit?: string;
  language?: string;
  fulfillmentType?: string;
};

export const loggerInstance = Logger.getInstance(logObject('backOfficeProductApi'));

export const backOfficeProductApi = {
  async getProductBySearchCriteria(
    { productId, itemCode, language, projections = ProjectionType.ProductListing, channel = '*' }: IProductAPI,
    options?: CustomRequestOptions,
  ): Promise<ProductData | Products | undefined> {
    if (!productId && !itemCode) {
      loggerInstance.error(
        'Missing productId or itemCode. Please check arguments for a valid product ID or alias code.',
      );
    }
    try {
      const requestObj = getProductByProductIdOrItemCode(API_VERSION_V1, language, channel);
      requestObj.params = {
        'channel-fallback-require': 'false',
        ...(productId && { projections }),
        ...(productId && { ids: productId }),
        ...(itemCode && { itemNumbers: itemCode }),
      };
      return await handleRequest<ProductData | Products>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async getProductByPageNumber(
    { type, page, limit, projections = ProjectionType.Catalog }: IProductPaginationAPI,
    options?: CustomRequestOptions,
  ): Promise<ProductData> {
    try {
      const requestObj = getProductSearchReqObj(API_VERSION_V1);
      requestObj.params = {
        projections,
        ...(page && { page }),
        ...(limit && { limit }),
        ...(type && { type }),
      };
      return await handleRequest<ProductData>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async getProductInventoryList(
    {
      itemNumber,
      locationId,
      lastEvaluatedId,
      projection,
      country = 'th',
      limit,
      language = EN_US,
      channel = 'DEFAULT',
      vertical = 'DEFAULT',
      fulfillmentType,
    }: IProductInventoryAPI,
    options?: CustomRequestOptions,
  ): Promise<ResponseInventories> {
    try {
      const requestObj = getInventoryList(API_VERSION_V2, itemNumber, language, country, channel, vertical);
      requestObj.params = {
        country,
        ...(projection && { projection }),
        ...(locationId && { locationId }),
        ...(lastEvaluatedId && { lastEvaluatedId }),
        ...(limit && { limit }),
        ...(fulfillmentType ? { fulfillmentType } : {}),
      };
      return await handleRequest<ResponseInventories>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async editFlowRedirectionToContentStack(
    { productId, language = EN_US, channel = 'DEFAULT' }: IProductAPI,
    options?: CustomRequestOptions,
  ): Promise<PcmMetadataResponse> {
    if (!productId) {
      loggerInstance.error('Missing productId. Please check arguments for a valid product ID.');
      throw new Error('Null argument. Please retry with valid product id.');
    }
    try {
      const requestObj = getRedirectionLinkForContentStack(API_VERSION_V1, productId, language, channel);
      return await handleRequest<PcmMetadataResponse>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async getPriceListByProductId(
    {
      page = '1',
      limit = '10',
      productId,
      itemNumber,
      countryCode,
      projections = ProjectionType.Price_Catalog,
      language,
      channel,
    }: IProductPriceAPI,
    options?: CustomRequestOptions,
  ): Promise<ItemResponse> {
    if (!productId || !itemNumber) {
      loggerInstance.error(
        'Missing productId or item number. Please check arguments for a valid product ID or item number.',
      );
      throw new Error('Null argument. Please retry with valid product id or item number.');
    }
    try {
      const requestObj = getPriceList(API_VERSION_V1, productId, itemNumber, countryCode, language, channel);
      requestObj.params = {
        page,
        limit,
        projections,
      };
      return await handleRequest<ItemResponse>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async getPublishChannelListByProductId(
    { productId, language }: IProductPriceAPI,
    options?: CustomRequestOptions,
  ): Promise<Products> {
    if (!productId) {
      loggerInstance.error('Missing productId. Please check arguments for a valid product ID.');
      throw new Error('Null argument. Please retry with valid product id.');
    }
    try {
      const requestObj = getChannelList(API_VERSION_V1, language);
      requestObj.params = {
        ids: productId,
      };
      return await handleRequest<Products>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async publishChannelByProductId(
    productId: string,
    payload: ProductPublishRequest,
    options?: CustomRequestOptions,
  ): Promise<ProductData> {
    if (!productId) {
      loggerInstance.error('Missing productId. Please check arguments for a valid product ID.');
      throw new Error('Null argument. Please retry with valid product id.');
    }
    try {
      const requestObj = publishChannel(API_VERSION_V1, productId);
      requestObj.body = payload as unknown as BodyInit;
      return await handleRequest<ProductData>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async duplicateChannelByProductId(
    productId: string,
    payload: ProductPublishRequest,
    options?: CustomRequestOptions,
  ): Promise<ProductData> {
    if (!productId) {
      loggerInstance.error('Missing productId. Please check arguments for a valid product ID.');
      throw new Error('Null argument. Please retry with valid product id.');
    }
    try {
      const requestObj = duplicateChannel(API_VERSION_V1, productId, payload.locale);
      requestObj.body = payload as unknown as BodyInit;
      return await handleRequest<ProductData>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async catalogListingByCategoryId(treeId: string, options?: CustomRequestOptions): Promise<ResponseCategories> {
    if (!treeId) {
      loggerInstance.error('Missing projection. Please check arguments for a valid projection.');
      throw new Error('Null argument. Please retry with valid projection.');
    }
    try {
      const requestObj = getCategoryList(API_VERSION_V1);
      requestObj.params = {
        treeId,
      };
      return await handleRequest<ResponseCategories>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async categoryOverviewDetails(
    category: string,
    projection: string,
    options?: CustomRequestOptions,
  ): Promise<Category> {
    if (!category) {
      loggerInstance.error('Missing category. Please check arguments for a valid category.');
      throw new Error('Null argument. Please retry with valid category.');
    }
    try {
      const requestObj = categoryOverview(API_VERSION_V1, category);
      requestObj.params = {
        projection,
      };
      return await handleRequest<Category>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async subCategoryDetails(category: string, projection: string, options?: CustomRequestOptions): Promise<Category[]> {
    if (!category) {
      loggerInstance.error('Missing category. Please check arguments for a valid category.');
      throw new Error('Null argument. Please retry with valid category.');
    }
    try {
      const requestObj = subCategory(API_VERSION_V1, category);
      requestObj.params = {
        projection,
      };
      return await handleRequest<Category[]>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create product request object. Message: ${err}`);
      throw err;
    }
  },

  async getCategoriesByIds(
    catIds: string,
    projections = ProjectionType.Min,
    language?: string,
    channel?: string,
    options?: CustomRequestOptions,
  ): Promise<ResponseCategories> {
    if (!catIds) {
      loggerInstance.error('Missing category. Please check arguments for a valid category.');
      throw new Error('Null argument. Please retry with valid category.');
    }
    try {
      const requestObj = getCategoryList(API_VERSION_V1, language, channel);
      requestObj.params = {
        projections,
        catids: catIds,
      };
      return await handleRequest<ResponseCategories>(requestObj, options);
    } catch (err) {
      loggerInstance.error(`Failed to create categories request object. Message: ${err}`);
      throw err;
    }
  },
};

export default backOfficeProductApi;
export type { IProductAPI, ProductData };
